<script setup lang="ts">
  /**
   * Page-level sibling of BfTabs: no container box, no dividers between tabs — just one
   * full-width hairline under the row, which reads as the top edge of the panel below.
   *
   * Use this to switch page sections (Overview / DNS Records / …); BfTabs stays for filter
   * groups sitting inside a card. Labels are sentence case at body size here, not the
   * uppercase micro-type of the bordered variant — these are section names, not filter chips.
   *
   * A real `role="tablist"` with roving tabindex and Left/Right arrow selection, as a tablist
   * is expected to behave.
   */
  import { ref } from 'vue'

  interface BfTabPlain {
    label: string
    /** Value emitted on select; defaults to `label` when omitted. */
    value?: string
    /** Optional count badge shown after the label. */
    count?: number
  }

  const props = withDefaults(
    defineProps<{
      tabs: BfTabPlain[]
      modelValue?: string
      ariaLabel?: string
    }>(),
    { modelValue: '', ariaLabel: 'Tabs' },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

  const tabRefs = ref<HTMLButtonElement[]>([])

  const resolveValue = (tab: BfTabPlain): string => tab.value ?? tab.label

  const isActive = (tab: BfTabPlain): boolean => props.modelValue === resolveValue(tab)

  /** Roving tabindex: only the selected tab is in the tab order, the rest are arrow-reachable. */
  const tabIndexFor = (tab: BfTabPlain): number => (isActive(tab) ? 0 : -1)

  const selectAt = (index: number): void => {
    const tab = props.tabs[index]
    if (!tab) return

    emit('update:modelValue', resolveValue(tab))
    tabRefs.value[index]?.focus()
  }

  const onArrowKey = (event: KeyboardEvent, index: number): void => {
    const step = event.key === 'ArrowRight' ? 1 : -1
    event.preventDefault()
    selectAt((index + step + props.tabs.length) % props.tabs.length)
  }
</script>

<template>
  <div class="tabs" role="tablist" :aria-label="ariaLabel">
    <button
      v-for="(tab, index) in tabs"
      :key="resolveValue(tab)"
      ref="tabRefs"
      type="button"
      class="tabs__tab"
      role="tab"
      :aria-selected="isActive(tab)"
      :tabindex="tabIndexFor(tab)"
      @click="$emit('update:modelValue', resolveValue(tab))"
      @keydown.left="onArrowKey($event, index)"
      @keydown.right="onArrowKey($event, index)"
    >
      {{ tab.label }}
      <span v-if="tab.count !== undefined" class="tabs__count">{{ tab.count }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
  .tabs {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    box-shadow: inset 0 -1px 0 0 var(--bf-line);
  }

  .tabs__tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 14px;
    box-sizing: border-box;
    background: none;
    border: none;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-sm);
    font-weight: var(--bf-fw-semibold);
    line-height: 1;
    color: var(--bf-fg-2);
    cursor: pointer;
    /* The indicator is a box-shadow, not a border, so it can overlap the row's own hairline
       exactly instead of stacking 1px above it. */
    box-shadow: inset 0 0 0 0 var(--bf-orange-dark);
    transition:
      color var(--bf-dur-base) var(--bf-ease),
      box-shadow var(--bf-dur-base) var(--bf-ease);
  }

  .tabs__tab:hover {
    color: var(--bf-orange-dark);
  }

  .tabs__tab[aria-selected='true'] {
    color: var(--bf-orange-dark);
    box-shadow: inset 0 -2px 0 0 var(--bf-orange-dark);
  }

  .tabs__tab:focus-visible {
    outline: 2px solid var(--bf-orange-dark);
    outline-offset: -4px;
    border-radius: var(--bf-r-sm);
  }

  /* Optional trailing count, same pairing as the bordered variant. */
  .tabs__count {
    padding: 2px 7px;
    border-radius: var(--bf-r-sm);
    background: #eef0f3;
    color: var(--bf-dusky-2);
    font-size: var(--bf-fs-2xs);
    letter-spacing: var(--bf-tr-wide);
  }

  .tabs__tab[aria-selected='true'] .tabs__count {
    background: #fde8d8;
    color: var(--bf-orange-dark);
  }

  /* --bf-bp-md */
  @media (max-width: 700px) {
    /* Scrolls rather than wraps — a wrapped row would break the single hairline the panel
       below aligns to. */
    .tabs {
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }

    .tabs::-webkit-scrollbar {
      display: none;
    }

    .tabs__tab {
      flex: 0 0 auto;
      white-space: nowrap;
    }
  }
</style>
